// mission-workspace.ts
//
// Dựng workspace cho một mission: worktree cách ly + pack GHIM + symlink farm +
// settings mission theo profile tool §3.2 + ignore entries + entry registry managed.
//
// Ba điều KHÔNG được làm (§9.4, D06):
//  1. Không đụng checkout/HEAD của source repo (worktree init lo phần đó).
//  2. Không copy `permissions.allow` từ template — quyền worker đến từ
//     `--allowedTools` trên argv (P2); `permissions.allow` chỉ xuất hiện khi
//     cờ `permissions_allow_fallback=true` được truyền VÀO (module này không
//     tự đọc config/manifest — P5 `autopilot init` mới là nơi load).
//  3. Không tự đọc `~/.avatar/*`: mọi path đều là tham số (test tiêm tmp dir).

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { ensureDir, pathExists, writeJsonAtomic } from "../filesystem-helpers.js";
import { installPackFromLocalDir } from "../install-pack-from-local-dir.js";
import { syncAllMountDirs } from "../symlink-farm-for-team-pack-mount-dirs.js";
import { withSettingsFileLock } from "../with-settings-file-lock.js";
import {
  DEFAULT_REGISTRY_PATH,
  recordManagedMissionWorkspace,
} from "../workspace-registry-store.js";
import { writeDangerousCommandGuardOptInFlag } from "./dangerous-command-guard-opt-in.js";
import { initMissionWorktree, type WorktreeInitResult } from "./mission-initializer-worktree.js";
import {
  buildMissionSettings,
  buildToolState,
  computeSettingsProfileDigest,
  type MissionSettingsInput,
} from "./mission-settings-profile.js";
import { buildMissionHooks } from "./mission-workspace-hooks.js";

// Re-export để call site (và test) chỉ cần nhớ MỘT module cho "mission workspace".
export {
  AUTOPILOT_REQUIRED_HOOK_FILES,
  buildMissionSettings,
  computeSettingsProfileDigest,
  MISSION_TOOLS_DISABLED,
  MISSION_TOOLS_ENABLED,
  type MissionHooks,
  type MissionSettings,
} from "./mission-settings-profile.js";
export { buildMissionHooks } from "./mission-workspace-hooks.js";

/**
 * Ignore entries CHÍNH XÁC của worktree mission — không hơn không kém (test 45).
 *
 * `.avatar-session.lock*` phải ignore được để không lỡ commit, NHƯNG sự XUẤT HIỆN của
 * nó ở gốc worktree lại là BẰNG CHỨNG worker đã thử mutate git (§9.4) ⇒ verifier xếp
 * loại vi phạm policy, KHÔNG âm thầm dọn.
 */
// `/.claude/state/` (Bug E, dogfood vòng 4): writer DUY NHẤT dưới thư mục này
// là autopilot-binding-file-writer.ts (controller-owned) — không ticket nào
// được đụng qua `allowed_paths`. Thiếu trong `.gitignore`, một binding sót lại
// từ attempt `contract_error` sẽ lọt `git ls-files --others` rồi bị `run` sau
// quét vào bootstrap commit, đọc thành scope violation giả. Xem
// controller-owned-paths.ts (lọc phía verifier, cùng mục đích).
export const MISSION_IGNORE_ENTRIES = [
  "/.avatar/attempt-output/",
  "/.avatar/status.html",
  "/.avatar-session.lock*",
  "/.claude/state/",
] as const;

export interface MissionWorkspaceInput extends MissionSettingsInput {
  sourceRepo: string;
  worktreePath: string;
  missionId: string;
  /** Thư mục pack nguồn để GHIM (copy) vào mission — pack bất biến suốt mission. */
  packSourceDir: string;
  registryPath?: string;
  now?: () => Date;
  /** base_sha đã validate (preflight); undefined ⇒ HEAD. */
  baseSha?: string;
}

export interface MissionWorkspaceResult {
  worktree: WorktreeInitResult;
  settingsPath: string;
  toolStatePath: string;
  /** sha256 của settings mission + profile tool đã áp (spec §3.2, N36). */
  settingsProfileSha256: string;
  mountDirs: string[];
}

export async function createMissionWorkspace(
  input: MissionWorkspaceInput,
): Promise<MissionWorkspaceResult> {
  const now = input.now ?? (() => new Date());

  // 1. Worktree cách ly (không đụng HEAD/checkout source).
  const worktree = await initMissionWorktree(
    input.sourceRepo,
    input.worktreePath,
    input.missionId,
    undefined,
    input.baseSha,
  );

  // 2. GHIM pack vào trong mission workspace (bản copy riêng, bất biến suốt mission).
  const claudeDir = join(input.worktreePath, ".claude");
  const packDir = join(claudeDir, "pack");
  await ensureDir(claudeDir);
  await installPackFromLocalDir(input.packSourceDir, packDir);

  // 3. Symlink farm 7 mount dir — dùng lại đúng hàm của đường interactive.
  const mounts = await syncAllMountDirs(packDir, claudeDir, true);

  // 4. Settings mission + state tool theo profile. `withSettingsFileLock` giữ
  // nguyên dù worktree vừa tạo (audit gate #164: MỌI writer settings.json qua
  // bracket, không miễn trừ theo ca). F4: hooks dựng ở tầng I/O này rồi truyền
  // xuống, giữ `buildMissionSettings` thuần.
  const settings = buildMissionSettings({
    ...input,
    hooks: input.hooks ?? (await buildMissionHooks(packDir)),
  });
  const settingsPath = join(claudeDir, "settings.json");
  const toolState = buildToolState(now());
  const toolStatePath = join(claudeDir, "avatar-tools.json");
  await withSettingsFileLock(input.worktreePath, async () => {
    await writeJsonAtomic(settingsPath, settings, 0o600);
    await writeJsonAtomic(toolStatePath, toolState);
  });

  // 5. Ignore entries CHÍNH XÁC — ghi vào `.gitignore` TRONG worktree (xem ghi chú
  //    ở `writeWorktreeExcludes`: git chỉ đọc info/exclude của common dir).
  await writeWorktreeExcludes(input.worktreePath);

  // 5b. team-ai-pack origin/main (7a29c58): pretooluse-dangerous-command-guard-hook.py
  // is now OPT-IN — `guard_enabled()` reads this exact flag file and `exit 0`s BEFORE
  // reading stdin when it's absent, silently disabling the autopilot deny-by-binding
  // guard (P1/spec §21/F17). Path is controller-owned (already covered by
  // `.claude/state/**` in CONTROLLER_OWNED_PATH_GLOBS and by MISSION_IGNORE_ENTRIES
  // above — no ticket's `allowed_paths` should ever need to touch it).
  await writeDangerousCommandGuardOptInFlag(claudeDir);

  // 6. Digest settings + profile để P5 doctor đưa vào compatibility manifest (N36).
  const settingsProfileSha256 = computeSettingsProfileDigest(settings, toolState);

  // 7. Registry: đánh dấu workspace này là MANAGED để P9 `sync --all` bỏ qua.
  await recordManagedMissionWorkspace(
    input.worktreePath,
    input.missionId,
    input.registryPath ?? DEFAULT_REGISTRY_PATH,
    now(),
  );

  return {
    worktree,
    settingsPath,
    toolStatePath,
    settingsProfileSha256,
    mountDirs: mounts.map((m) => m.dir),
  };
}

/**
 * Ghi ignore vào `.gitignore` ở GỐC WORKTREE (không phải per-worktree
 * `info/exclude` — ĐO ĐƯỢC 2026-09-09: git chỉ đọc bản ở common dir, dùng
 * bản đó lại vi phạm F09 vì file nằm trong `.git` của người dùng). File này
 * KHÔNG được commit — hạ tầng mission, chỉ sống trong worktree.
 *
 * Exported (không chỉ gọi từ createMissionWorkspace) để fix dedupe (dogfood
 * 260925) có unit test trực tiếp.
 */
export async function writeWorktreeExcludes(worktreePath: string): Promise<void> {
  const gitignorePath = join(worktreePath, ".gitignore");
  const existing = (await pathExists(gitignorePath))
    ? await fs.readFile(gitignorePath, "utf8")
    : "";
  // Dedupe against a seed `.gitignore` that already has one of our entries
  // (bug found dogfooding 2026-09-25) — whole-line compare, cosmetic but cheap.
  const existingLines = new Set(existing.split("\n"));
  const toAppend = MISSION_IGNORE_ENTRIES.filter((e) => !existingLines.has(e));
  if (toAppend.length === 0) return;
  const block = `${toAppend.join("\n")}\n`;
  const next =
    existing === "" || existing.endsWith("\n") ? existing + block : `${existing}\n${block}`;
  await fs.writeFile(gitignorePath, next, "utf8");
}

/**
 * `.avatar-session.lock` ở GỐC worktree = worker đã thử mutate git ⇒ VI PHẠM POLICY,
 * không phải file ứng dụng. Không tự dọn: sự tồn tại của nó là tín hiệu (§9.4).
 */
export async function detectLegacySessionLockViolation(
  worktreePath: string,
): Promise<{ violation: boolean; files: string[] }> {
  const names = await fs.readdir(worktreePath).catch(() => [] as string[]);
  const files = names.filter(
    (n) => n === ".avatar-session.lock" || n.startsWith(".avatar-session.lock.tmp."),
  );
  return { violation: files.length > 0, files };
}
